import React, { useMemo, useRef, useState } from 'react';
import { useProfile } from './profile.hooks';
import {
  Panel,
  FormGrid,
  Field,
  FieldLabel,
  Input,
  PhotoRow,
  AvatarCircle,
  ChangeLink,
  NameRow,
} from './profile.styled';
import { UploadImageModal } from './upload-image/upload-image';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { AppAvatar } from '../../../../components/avatar/avatar';

export const ProfileSettings: React.FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    firstName,
    lastName,
    title,
    nickname,
    company,
    state,
    initials,
    handleFirstNameChange,
    handleFirstNameBlur,
    handleLastNameChange,
    handleLastNameBlur,
    handleNicknameChange,
    handleNicknameBlur,
    handleCompanyChange,
    handleCompanyBlur,
    handleTitleChange,
    handleTitleBlur,
    handleStateChange,
    handleStateBlur,
    handlePhotoUpload,
  } = useProfile();

  const { profile } = useAuth();

  const handlePhotoClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    // Reset input (so selecting the same file twice still fires change)
    event.target.value = '';

    if (!file) return;

    setSelectedFile(file);
    setIsUploadOpen(true);
  };

  const handleModalClose = () => {
    setIsUploadOpen(false);
    setSelectedFile(null);
  };

  const handleModalSave = async (jpegFile: File) => {
    await handlePhotoUpload(jpegFile);
    handleModalClose();
  };

  const fallbackInitials = useMemo(() => String(initials ?? '?'), [initials]);

  return (
    <Panel>
      <FormGrid>
        <NameRow>
          <Field>
            <FieldLabel htmlFor="first_name">First name</FieldLabel>
            <Input
              id="first_name"
              value={firstName}
              onChange={handleFirstNameChange}
              onBlur={handleFirstNameBlur}
              placeholder="First name"
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="last_name">Last name</FieldLabel>
            <Input
              id="last_name"
              value={lastName}
              onChange={handleLastNameChange}
              onBlur={handleLastNameBlur}
              placeholder="Last name"
            />
          </Field>
        </NameRow>

        <Field>
          <FieldLabel htmlFor="nickname">Nickname</FieldLabel>
          <Input
            id="nickname"
            value={nickname}
            onChange={handleNicknameChange}
            onBlur={handleNicknameBlur}
            placeholder="Nickname"
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="company">Company</FieldLabel>
          <Input
            id="company"
            value={company}
            onChange={handleCompanyChange}
            onBlur={handleCompanyBlur}
            placeholder="Company"
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="title">Title</FieldLabel>
          <Input
            id="title"
            value={title}
            onChange={handleTitleChange}
            onBlur={handleTitleBlur}
            placeholder="Title"
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="state">State</FieldLabel>
          <Input
            id="state"
            value={state}
            onChange={handleStateChange}
            onBlur={handleStateBlur}
            placeholder="State"
          />
        </Field>

        <Field>
          <FieldLabel>Photo</FieldLabel>
          <PhotoRow>
            <AppAvatar
              userId={String(profile?.id ?? '')}
              version={(profile as any)?.avatar_version ?? (profile as any)?.updated_at ?? null}
              hasAvatar={!!profile?.avatar_url}
              alt="You"
              initials={initials}
              width={34}
              height={34}
              imgStyle={{ borderRadius: '999px', objectFit: 'cover' }}
              fallbackStyle={{ borderRadius: '999px' }}
            />

            {/* Keep original initials component present (unchanged usage expectations) */}
            <AvatarCircle style={{ display: 'none' }}>{fallbackInitials}</AvatarCircle>

            <ChangeLink role="button" tabIndex={0} onClick={handlePhotoClick}>
              Click to change
            </ChangeLink>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />
          </PhotoRow>
        </Field>
      </FormGrid>

      <UploadImageModal
        open={isUploadOpen}
        file={selectedFile}
        onOpenChange={(v) => {
          if (!v) handleModalClose();
          else setIsUploadOpen(true);
        }}
        onSave={handleModalSave}
      />
    </Panel>
  );
};
